// frontend/src/pages/TimeCodesPage.jsx
import React, { useEffect, useState } from "react";
import {
  getTimeCodes,
  createTimeCode,
  updateTimeCode,
  deleteTimeCode,
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
  getTaskTypes,
  createTaskType,
  updateTaskType,
  deleteTaskType,
} from "../api";

export default function TimeCodesPage({ user }) {
  const [timeCodes, setTimeCodes] = useState([]);
  const [statuses, setStatuses] = useState({ general: [], tax_filing: [], priority: [] });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [activeTab, setActiveTab] = useState("timecodes"); // "timecodes", "statuses", or "tasktypes"

  // Status management state
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [showAddStatus, setShowAddStatus] = useState(false);
  const [statusType, setStatusType] = useState("general");

  // Task Types management state
  const [taskTypes, setTaskTypes] = useState([]);
  const [newTaskTypeName, setNewTaskTypeName] = useState("");
  const [newTaskTypeCategory, setNewTaskTypeCategory] = useState("");
  const [draggingTypeId, setDraggingTypeId] = useState(null);
  // Which task-type row is being renamed, and the in-progress name.
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [editingTypeName, setEditingTypeName] = useState("");

  async function saveTaskTypeName(id) {
    const v = editingTypeName.trim();
    if (!v) return; // don't save a blank name
    try {
      await updateTaskType(id, { name: v });
      const list = await getTaskTypes();
      setTaskTypes(Array.isArray(list) ? list : list.results || []);
      setEditingTypeId(null);
      setEditingTypeName("");
    } catch (err) {
      alert(`Update failed: ${err.message || err}`);
    }
  }

  function cancelEditTaskType() {
    setEditingTypeId(null);
    setEditingTypeName("");
  }

  // Drag-to-reorder task types. Moves `draggedId` to `targetId`'s slot, then
  // renumbers sort_order sequentially and PATCHes only the rows that actually
  // moved. Optimistic; refetches on failure.
  async function handleReorderTaskType(draggedId, targetId) {
    if (!draggedId || draggedId === targetId) return;

    const from = taskTypes.findIndex((t) => t.id === draggedId);
    const to = taskTypes.findIndex((t) => t.id === targetId);
    if (from === -1 || to === -1) return;

    const next = [...taskTypes];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);

    const renumbered = next.map((t, i) => ({ ...t, sort_order: i }));
    const changed = renumbered.filter((t, i) => t.sort_order !== taskTypes.find((o) => o.id === t.id).sort_order);

    setTaskTypes(renumbered);
    try {
      await Promise.all(
        changed.map((t) => updateTaskType(t.id, { sort_order: t.sort_order }))
      );
    } catch (err) {
      alert(`Reorder failed: ${err.message || err}`);
      const list = await getTaskTypes();
      setTaskTypes(Array.isArray(list) ? list : list.results || []);
    }
  }

  const isManager = ["MANAGER", "ADMIN"].includes(String(user?.role).toUpperCase());

  const [form, setForm] = useState({
    code: "",
    description: "",
    default_rate: "0.00",
  });

  const [statusForm, setStatusForm] = useState({
    name: "",
    color: "#3b82f6",
    status_type: "general",
    order: 0
  });

  async function loadTimeCodes() {
    setErr("");
    try {
      const data = await getTimeCodes();
      setTimeCodes(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      setErr(e.message);
    }
  }

  async function loadStatuses() {
    setErr("");
    try {
      const data = await apiGet("/status-options/by_type/");
      setStatuses(data);
    } catch (e) {
      setErr(e.message);
    }
  }

  useEffect(() => {
    loadTimeCodes();
    loadStatuses();
  }, []);

  useEffect(() => {
    if (activeTab === "tasktypes") {
      getTaskTypes()
        .then((data) => setTaskTypes(Array.isArray(data) ? data : data.results || []))
        .catch(() => setTaskTypes([]));
    }
  }, [activeTab]);

  async function handleCreate(e) {
    e.preventDefault();
    if (!isManager) return;

    setBusy(true);
    setErr("");
    try {
      await createTimeCode(form);
      setForm({ code: "", description: "", default_rate: "0.00" });
      setShowAdd(false);
      await loadTimeCodes();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleUpdate(tc) {
    if (!isManager) return;

    setBusy(true);
    setErr("");
    try {
      await updateTimeCode(tc.id, {
        code: tc.code,
        description: tc.description,
        default_rate: tc.default_rate,
        is_active: tc.is_active,
      });
      await loadTimeCodes();
      setEditingId(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id) {
    if (!isManager || !window.confirm("Delete this time code?")) return;

    setBusy(true);
    setErr("");
    try {
      await deleteTimeCode(id);
      await loadTimeCodes();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(tc) {
    if (!isManager) return;

    setBusy(true);
    setErr("");
    try {
      await updateTimeCode(tc.id, {
        code: tc.code,
        description: tc.description,
        default_rate: tc.default_rate,
        is_active: !tc.is_active,
      });
      await loadTimeCodes();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  // Status Management Functions
  async function handleCreateStatus(e) {
    e.preventDefault();
    if (!isManager) return;

    setBusy(true);
    setErr("");
    try {
      await apiPost("/status-options/", statusForm);
      setStatusForm({ name: "", color: "#3b82f6", status_type: "general", order: 0 });
      setShowAddStatus(false);
      await loadStatuses();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleUpdateStatus(status) {
    if (!isManager) return;

    setBusy(true);
    setErr("");
    try {
      await apiPut(`/status-options/${status.id}/`, {
        name: status.name,
        color: status.color,
        status_type: status.type || status.status_type, // Handle both field names
        order: status.order,
        is_active: status.is_active
      });
      await loadStatuses();
      setEditingStatusId(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteStatus(id) {
    if (!isManager || !window.confirm("Delete this status? This may affect existing clients.")) return;

    setBusy(true);
    setErr("");
    try {
      await apiDelete(`/status-options/${id}/`);
      await loadStatuses();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleStatusActive(status) {
    if (!isManager) return;

    setBusy(true);
    setErr("");
    try {
      await apiPut(`/status-options/${status.id}/`, {
        name: status.name,
        color: status.color,
        status_type: status.type || status.status_type, // Handle both field names
        order: status.order,
        is_active: !status.is_active
      });
      await loadStatuses();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!isManager) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Access Denied</h2>
        <p>Only managers can access this page.</p>
      </div>
    );
  }

  const currentStatuses = statuses[statusType] || [];

  return (
    <div style={{ padding: "30px 40px", maxWidth: 1600, margin: "0 auto" }}>
      {/* Tab Navigation */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "2px solid #e5e7eb" }}>
        <button
          onClick={() => setActiveTab("timecodes")}
          style={{
            padding: "12px 24px",
            border: "none",
            borderBottom: activeTab === "timecodes" ? "3px solid #2563eb" : "3px solid transparent",
            background: "transparent",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 15,
            color: activeTab === "timecodes" ? "#2563eb" : "#6b7280",
            marginBottom: -2,
            transition: "all 0.2s ease"
          }}
        >
          Time Codes
        </button>
        <button
          onClick={() => setActiveTab("statuses")}
          style={{
            padding: "12px 24px",
            border: "none",
            borderBottom: activeTab === "statuses" ? "3px solid #2563eb" : "3px solid transparent",
            background: "transparent",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 15,
            color: activeTab === "statuses" ? "#2563eb" : "#6b7280",
            marginBottom: -2,
            transition: "all 0.2s ease"
          }}
        >
          Status Options
        </button>
        <button
          onClick={() => setActiveTab("tasktypes")}
          style={{
            padding: "12px 24px",
            border: "none",
            borderBottom: activeTab === "tasktypes" ? "3px solid #2563eb" : "3px solid transparent",
            background: "transparent",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 15,
            color: activeTab === "tasktypes" ? "#2563eb" : "#6b7280",
            marginBottom: -2,
            transition: "all 0.2s ease"
          }}
        >
          Tasks
        </button>
      </div>

      {err && (
        <div
          style={{
            background: "#fee2e2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            padding: 12,
            borderRadius: 8,
            marginBottom: 20,
          }}
        >
          {err}
        </div>
      )}

      {/* TIME CODES TAB */}
      {activeTab === "timecodes" && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 600 }}>Time Codes</h1>
            <button
              onClick={() => setShowAdd(true)}
              style={{
                padding: "12px 20px",
                borderRadius: 8,
                border: "none",
                background: "#10b981",
                color: "white",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              + Add Time Code
            </button>
          </div>

          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            {/* Table Header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "200px 1fr 180px 140px 180px",
                padding: "16px 24px",
                fontWeight: 600,
                background: "#f9fafb",
                borderBottom: "2px solid #e5e7eb",
                fontSize: 14,
                color: "#374151",
              }}
            >
              <div>Code</div>
              <div>Description</div>
              <div style={{ textAlign: "right" }}>Default Rate</div>
              <div style={{ textAlign: "center" }}>Status</div>
              <div style={{ textAlign: "center" }}>Actions</div>
            </div>

            {/* Table Rows */}
            {timeCodes.map((tc, idx) => {
              const isEditing = editingId === tc.id;
              return (
                <div
                  key={tc.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "200px 1fr 180px 140px 180px",
                    padding: "16px 24px",
                    borderBottom: idx < timeCodes.length - 1 ? "1px solid #f3f4f6" : "none",
                    alignItems: "center",
                    fontSize: 14,
                    background: "white",
                  }}
                >
                  <div>
                    {isEditing ? (
                      <input
                        value={tc.code}
                        onChange={(e) => {
                          const copy = [...timeCodes];
                          copy[idx] = { ...tc, code: e.target.value };
                          setTimeCodes(copy);
                        }}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 6,
                          border: "1px solid #d1d5db",
                          width: "100%",
                          fontSize: 14,
                        }}
                      />
                    ) : (
                      <span style={{ fontWeight: 600, color: "#111827" }}>{tc.code}</span>
                    )}
                  </div>
                  <div>
                    {isEditing ? (
                      <input
                        value={tc.description}
                        onChange={(e) => {
                          const copy = [...timeCodes];
                          copy[idx] = { ...tc, description: e.target.value };
                          setTimeCodes(copy);
                        }}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 6,
                          border: "1px solid #d1d5db",
                          width: "100%",
                          fontSize: 14,
                        }}
                      />
                    ) : (
                      <span style={{ color: "#6b7280" }}>{tc.description || "—"}</span>
                    )}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.01"
                        value={tc.default_rate}
                        onChange={(e) => {
                          const copy = [...timeCodes];
                          copy[idx] = { ...tc, default_rate: e.target.value };
                          setTimeCodes(copy);
                        }}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 6,
                          border: "1px solid #d1d5db",
                          width: "100%",
                          fontSize: 14,
                          textAlign: "right",
                        }}
                      />
                    ) : (
                      <span style={{ fontWeight: 600, color: "#111827" }}>
                        ${parseFloat(tc.default_rate || 0).toFixed(2)}
                      </span>
                    )}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <span
                      onClick={() => !isEditing && toggleActive(tc)}
                      style={{
                        padding: "4px 12px",
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 600,
                        background: tc.is_active ? "#d1fae5" : "#fee2e2",
                        color: tc.is_active ? "#065f46" : "#991b1b",
                        cursor: isEditing ? "default" : "pointer",
                        display: "inline-block",
                      }}
                    >
                      {tc.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                    {!isEditing ? (
                      <>
                        <button
                          onClick={() => setEditingId(tc.id)}
                          style={{
                            padding: "6px 16px",
                            borderRadius: 6,
                            border: "1px solid #d1d5db",
                            background: "white",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 500,
                            color: "#374151",
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(tc.id)}
                          disabled={busy}
                          style={{
                            padding: "6px 16px",
                            borderRadius: 6,
                            border: "none",
                            background: "#fee2e2",
                            color: "#991b1b",
                            cursor: busy ? "not-allowed" : "pointer",
                            fontSize: 13,
                            fontWeight: 500,
                          }}
                        >
                          Delete
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleUpdate(tc)}
                          disabled={busy}
                          style={{
                            padding: "6px 16px",
                            borderRadius: 6,
                            border: "none",
                            background: "#2563eb",
                            color: "white",
                            cursor: busy ? "not-allowed" : "pointer",
                            fontSize: 13,
                            fontWeight: 500,
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingId(null);
                            loadTimeCodes();
                          }}
                          style={{
                            padding: "6px 16px",
                            borderRadius: 6,
                            border: "1px solid #d1d5db",
                            background: "white",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 500,
                            color: "#374151",
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            {timeCodes.length === 0 && (
              <div style={{ padding: 60, textAlign: "center", color: "#9ca3af" }}>
                No time codes yet. Click "+ Add Time Code" to create one.
              </div>
            )}
          </div>

          {/* Add Modal */}
          {showAdd && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
              onClick={() => setShowAdd(false)}
            >
              <div
                style={{
                  background: "white",
                  borderRadius: 12,
                  padding: 32,
                  maxWidth: 500,
                  width: "90%",
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 style={{ marginTop: 0, marginBottom: 24, fontSize: 20, fontWeight: 600 }}>
                  Add Time Code
                </h3>
                <form onSubmit={handleCreate} style={{ display: "grid", gap: 20 }}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 6,
                      }}
                    >
                      Code *
                    </label>
                    <input
                      placeholder="e.g., TAX"
                      value={form.code}
                      onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                      style={{
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        width: "100%",
                        fontSize: 14,
                      }}
                      required
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 6,
                      }}
                    >
                      Description
                    </label>
                    <input
                      placeholder="e.g., Tax preparation"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      style={{
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        width: "100%",
                        fontSize: 14,
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 6,
                      }}
                    >
                      Default Rate *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={form.default_rate}
                      onChange={(e) => setForm({ ...form, default_rate: e.target.value })}
                      style={{
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        width: "100%",
                        fontSize: 14,
                      }}
                      required
                    />
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                    <button
                      type="submit"
                      disabled={busy}
                      style={{
                        flex: 1,
                        padding: "12px 16px",
                        borderRadius: 8,
                        border: "none",
                        background: "#2563eb",
                        color: "white",
                        cursor: busy ? "not-allowed" : "pointer",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {busy ? "Creating..." : "Create Time Code"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAdd(false)}
                      style={{
                        flex: 1,
                        padding: "12px 16px",
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        background: "white",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: 14,
                        color: "#374151",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      )}

      {/* STATUS OPTIONS TAB */}
      {activeTab === "statuses" && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 28, fontWeight: 600, marginBottom: 8 }}>Status Options</h1>
              <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>
                Manage dropdown options for client statuses, priorities, and tax filing statuses
              </p>
            </div>
            <button
              onClick={() => setShowAddStatus(true)}
              style={{
                padding: "12px 20px",
                borderRadius: 8,
                border: "none",
                background: "#10b981",
                color: "white",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              + Add Status Option
            </button>
          </div>

          {/* Status Type Filter */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            <button
              onClick={() => setStatusType("general")}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: statusType === "general" ? "2px solid #2563eb" : "1px solid #d1d5db",
                background: statusType === "general" ? "#eff6ff" : "white",
                color: statusType === "general" ? "#2563eb" : "#374151",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              General Status ({statuses.general?.length || 0})
            </button>
            <button
              onClick={() => setStatusType("tax_filing")}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: statusType === "tax_filing" ? "2px solid #2563eb" : "1px solid #d1d5db",
                background: statusType === "tax_filing" ? "#eff6ff" : "white",
                color: statusType === "tax_filing" ? "#2563eb" : "#374151",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              Tax Filing Status ({statuses.tax_filing?.length || 0})
            </button>
            {/* The Priority status type is retired — priority is a boolean flag
                on Task now, so nothing reads these rows. */}
          </div>

          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            {/* Table Header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "60px 1fr 140px 100px 140px 200px",
                padding: "16px 24px",
                fontWeight: 600,
                background: "#f9fafb",
                borderBottom: "2px solid #e5e7eb",
                fontSize: 14,
                color: "#374151",
              }}
            >
              <div style={{ textAlign: "center" }}>Order</div>
              <div>Name</div>
              <div>Color</div>
              <div style={{ textAlign: "center" }}>Type</div>
              <div style={{ textAlign: "center" }}>Status</div>
              <div style={{ textAlign: "center" }}>Actions</div>
            </div>

            {/* Table Rows */}
            {currentStatuses.map((status, idx) => {
              const isEditing = editingStatusId === status.id;
              return (
                <div
                  key={status.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "60px 1fr 140px 100px 140px 200px",
                    padding: "16px 24px",
                    borderBottom: idx < currentStatuses.length - 1 ? "1px solid #f3f4f6" : "none",
                    alignItems: "center",
                    fontSize: 14,
                    background: "white",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    {isEditing ? (
                      <input
                        type="number"
                        value={status.order}
                        onChange={(e) => {
                          const copy = [...currentStatuses];
                          copy[idx] = { ...status, order: parseInt(e.target.value) || 0 };
                          setStatuses({ ...statuses, [statusType]: copy });
                        }}
                        style={{
                          padding: "6px",
                          borderRadius: 6,
                          border: "1px solid #d1d5db",
                          width: "50px",
                          fontSize: 13,
                          textAlign: "center",
                        }}
                      />
                    ) : (
                      <span style={{ color: "#6b7280" }}>{status.order}</span>
                    )}
                  </div>
                  <div>
                    {isEditing ? (
                      <input
                        value={status.name}
                        onChange={(e) => {
                          const copy = [...currentStatuses];
                          copy[idx] = { ...status, name: e.target.value };
                          setStatuses({ ...statuses, [statusType]: copy });
                        }}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 6,
                          border: "1px solid #d1d5db",
                          width: "100%",
                          fontSize: 14,
                        }}
                      />
                    ) : (
                      <span style={{ fontWeight: 600, color: "#111827" }}>{status.name}</span>
                    )}
                  </div>
                  <div>
                    {isEditing ? (
                      <input
                        type="color"
                        value={status.color}
                        onChange={(e) => {
                          const copy = [...currentStatuses];
                          copy[idx] = { ...status, color: e.target.value };
                          setStatuses({ ...statuses, [statusType]: copy });
                        }}
                        style={{
                          padding: "4px",
                          borderRadius: 6,
                          border: "1px solid #d1d5db",
                          width: "100%",
                          height: "36px",
                          cursor: "pointer"
                        }}
                      />
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: 4,
                            background: status.color,
                            border: "1px solid #d1d5db"
                          }}
                        />
                        <span style={{ fontSize: 12, color: "#6b7280" }}>{status.color}</span>
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: "center", fontSize: 12, color: "#6b7280", textTransform: "capitalize" }}>
                    {(status.type || status.status_type || "general").replace(/_/g, ' ')}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <span
                      onClick={() => !isEditing && toggleStatusActive(status)}
                      style={{
                        padding: "4px 12px",
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 600,
                        background: status.is_active ? "#d1fae5" : "#fee2e2",
                        color: status.is_active ? "#065f46" : "#991b1b",
                        cursor: isEditing ? "default" : "pointer",
                        display: "inline-block",
                      }}
                    >
                      {status.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                    {!isEditing ? (
                      <>
                        <button
                          onClick={() => setEditingStatusId(status.id)}
                          style={{
                            padding: "6px 16px",
                            borderRadius: 6,
                            border: "1px solid #d1d5db",
                            background: "white",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 500,
                            color: "#374151",
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteStatus(status.id)}
                          disabled={busy}
                          style={{
                            padding: "6px 16px",
                            borderRadius: 6,
                            border: "none",
                            background: "#fee2e2",
                            color: "#991b1b",
                            cursor: busy ? "not-allowed" : "pointer",
                            fontSize: 13,
                            fontWeight: 500,
                          }}
                        >
                          Delete
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleUpdateStatus(status)}
                          disabled={busy}
                          style={{
                            padding: "6px 16px",
                            borderRadius: 6,
                            border: "none",
                            background: "#2563eb",
                            color: "white",
                            cursor: busy ? "not-allowed" : "pointer",
                            fontSize: 13,
                            fontWeight: 500,
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingStatusId(null);
                            loadStatuses();
                          }}
                          style={{
                            padding: "6px 16px",
                            borderRadius: 6,
                            border: "1px solid #d1d5db",
                            background: "white",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 500,
                            color: "#374151",
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            {currentStatuses.length === 0 && (
              <div style={{ padding: 60, textAlign: "center", color: "#9ca3af" }}>
                No status options for this type yet. Click "+ Add Status Option" to create one.
              </div>
            )}
          </div>

          {/* Add Status Modal */}
          {showAddStatus && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
              onClick={() => setShowAddStatus(false)}
            >
              <div
                style={{
                  background: "white",
                  borderRadius: 12,
                  padding: 32,
                  maxWidth: 500,
                  width: "90%",
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 style={{ marginTop: 0, marginBottom: 24, fontSize: 20, fontWeight: 600 }}>
                  Add Status Option
                </h3>
                <form onSubmit={handleCreateStatus} style={{ display: "grid", gap: 20 }}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 6,
                      }}
                    >
                      Name *
                    </label>
                    <input
                      placeholder="e.g., In Progress"
                      value={statusForm.name}
                      onChange={(e) => setStatusForm({ ...statusForm, name: e.target.value })}
                      style={{
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        width: "100%",
                        fontSize: 14,
                      }}
                      required
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 6,
                      }}
                    >
                      Type *
                    </label>
                    <select
                      value={statusForm.status_type}
                      onChange={(e) => setStatusForm({ ...statusForm, status_type: e.target.value })}
                      style={{
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        width: "100%",
                        fontSize: 14,
                      }}
                      required
                    >
                      <option value="general">General Status</option>
                      <option value="tax_filing">Tax Filing Status</option>
                    </select>
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 6,
                      }}
                    >
                      Color *
                    </label>
                    <input
                      type="color"
                      value={statusForm.color}
                      onChange={(e) => setStatusForm({ ...statusForm, color: e.target.value })}
                      style={{
                        padding: "6px",
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        width: "100%",
                        height: "44px",
                        cursor: "pointer"
                      }}
                      required
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 6,
                      }}
                    >
                      Display Order
                    </label>
                    <input
                      type="number"
                      value={statusForm.order}
                      onChange={(e) => setStatusForm({ ...statusForm, order: parseInt(e.target.value) || 0 })}
                      style={{
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        width: "100%",
                        fontSize: 14,
                      }}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                    <button
                      type="submit"
                      disabled={busy}
                      style={{
                        flex: 1,
                        padding: "12px 16px",
                        borderRadius: 8,
                        border: "none",
                        background: "#2563eb",
                        color: "white",
                        cursor: busy ? "not-allowed" : "pointer",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {busy ? "Creating..." : "Create Status"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddStatus(false)}
                      style={{
                        flex: 1,
                        padding: "12px 16px",
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        background: "white",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: 14,
                        color: "#374151",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      )}

      {/* TASK TYPES TAB */}
      {activeTab === "tasktypes" && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 28, fontWeight: 600, marginBottom: 8 }}>Tasks</h1>
              <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>
                Manage the master list of task types available to assign to clients
              </p>
            </div>
          </div>

          {/* New Task Type form */}
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              marginBottom: 16,
              padding: 16,
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
            }}
          >
            <input
              type="text"
              placeholder="New task name…"
              value={newTaskTypeName}
              onChange={(e) => setNewTaskTypeName(e.target.value)}
              style={{ flex: 1, padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 4, fontSize: 14 }}
            />
            <select
              value={newTaskTypeCategory}
              onChange={(e) => setNewTaskTypeCategory(e.target.value)}
              style={{ padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 4, background: "white", fontSize: 14 }}
            >
              <option value="">Either</option>
              <option value="corporate">Corporate (T2)</option>
              <option value="personal">Personal (T1)</option>
              <option value="tslips">T-Slips</option>
            </select>
            <button
              onClick={async () => {
                if (!newTaskTypeName.trim()) return;
                try {
                  await createTaskType({
                    name: newTaskTypeName.trim(),
                    default_category: newTaskTypeCategory,
                  });
                  setNewTaskTypeName("");
                  setNewTaskTypeCategory("");
                  const list = await getTaskTypes();
                  setTaskTypes(Array.isArray(list) ? list : list.results || []);
                } catch (e) {
                  alert(`Failed to create: ${e.message || e}`);
                }
              }}
              style={{
                padding: "8px 16px",
                background: "#10b981",
                color: "white",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              + Add Task
            </button>
          </div>

          {/* List */}
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={{ padding: "16px 24px", textAlign: "left", fontSize: 14, fontWeight: 600, color: "#374151" }}>Name</th>
                  <th style={{ padding: "16px 24px", textAlign: "left", fontSize: 14, fontWeight: 600, color: "#374151" }}>Default Category</th>
                  <th style={{ padding: "16px 24px", textAlign: "left", fontSize: 14, fontWeight: 600, color: "#374151" }} title="Drag a row to reorder">Order</th>
                  <th style={{ padding: "16px 24px", textAlign: "center", fontSize: 14, fontWeight: 600, color: "#374151" }}>Active</th>
                  <th style={{ padding: "16px 24px", textAlign: "right", fontSize: 14, fontWeight: 600, color: "#374151" }}></th>
                </tr>
              </thead>
              <tbody>
                {taskTypes.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#9ca3af", fontSize: 14 }}>
                      No tasks yet. Add one above.
                    </td>
                  </tr>
                ) : (
                  taskTypes.map((tt, idx) => (
                    <tr
                      key={tt.id}
                      draggable={editingTypeId !== tt.id}
                      onDragStart={() => setDraggingTypeId(tt.id)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        handleReorderTaskType(draggingTypeId, tt.id);
                        setDraggingTypeId(null);
                      }}
                      onDragEnd={() => setDraggingTypeId(null)}
                      style={{
                        borderBottom: idx < taskTypes.length - 1 ? "1px solid #f3f4f6" : "none",
                        opacity: draggingTypeId === tt.id ? 0.4 : 1,
                        background: draggingTypeId && draggingTypeId !== tt.id ? "#fafafa" : "transparent",
                      }}
                    >
                      <td style={{ padding: "12px 24px" }}>
                        {editingTypeId === tt.id ? (
                          <input
                            type="text"
                            autoFocus
                            value={editingTypeName}
                            placeholder="Task name"
                            onChange={(e) => setEditingTypeName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveTaskTypeName(tt.id);
                              if (e.key === "Escape") cancelEditTaskType();
                            }}
                            style={{
                              width: "100%",
                              padding: "6px 8px",
                              border: "1px solid #2563eb",
                              borderRadius: 4,
                              background: "#fff",
                              fontSize: 14,
                            }}
                          />
                        ) : (
                          <span style={{ fontSize: 14, color: "#111827" }}>{tt.name}</span>
                        )}
                      </td>
                      <td style={{ padding: "12px 24px" }}>
                        <select
                          value={tt.default_category || ""}
                          onChange={async (e) => {
                            try {
                              await updateTaskType(tt.id, { default_category: e.target.value });
                              const list = await getTaskTypes();
                              setTaskTypes(Array.isArray(list) ? list : list.results || []);
                            } catch (err) {
                              alert(`Update failed: ${err.message || err}`);
                            }
                          }}
                          style={{ padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: 4, background: "white", fontSize: 14 }}
                        >
                          <option value="">Either</option>
                          <option value="corporate">Corporate (T2)</option>
                          <option value="personal">Personal (T1)</option>
                          <option value="tslips">T-Slips</option>
                        </select>
                      </td>
                      <td
                        style={{
                          padding: "12px 24px",
                          cursor: "grab",
                          color: "#9ca3af",
                          fontSize: 18,
                          userSelect: "none",
                        }}
                        title="Drag to reorder"
                      >
                        ⋮⋮
                      </td>
                      <td style={{ padding: "12px 24px", textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={tt.is_active}
                          onChange={async (e) => {
                            try {
                              await updateTaskType(tt.id, { is_active: e.target.checked });
                              const list = await getTaskTypes();
                              setTaskTypes(Array.isArray(list) ? list : list.results || []);
                            } catch (err) {
                              alert(`Update failed: ${err.message || err}`);
                            }
                          }}
                        />
                      </td>
                      <td style={{ padding: "12px 24px", textAlign: "right", whiteSpace: "nowrap" }}>
                        {editingTypeId === tt.id ? (
                          <>
                            <button
                              onClick={() => saveTaskTypeName(tt.id)}
                              style={{
                                padding: "4px 10px",
                                border: "1px solid #2563eb",
                                color: "#fff",
                                background: "#2563eb",
                                borderRadius: 4,
                                cursor: "pointer",
                                fontSize: 12,
                                fontWeight: 600,
                                marginRight: 8,
                              }}
                            >
                              Save changes
                            </button>
                            <button
                              onClick={cancelEditTaskType}
                              style={{
                                padding: "4px 10px",
                                border: "1px solid #d1d5db",
                                color: "#374151",
                                background: "#fff",
                                borderRadius: 4,
                                cursor: "pointer",
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setEditingTypeId(tt.id);
                                setEditingTypeName(tt.name);
                              }}
                              style={{
                                padding: "4px 10px",
                                border: "1px solid #d1d5db",
                                color: "#374151",
                                background: "#fff",
                                borderRadius: 4,
                                cursor: "pointer",
                                fontSize: 12,
                                fontWeight: 600,
                                marginRight: 8,
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Delete task "${tt.name}"?`)) return;
                                try {
                                  await deleteTaskType(tt.id);
                                  const list = await getTaskTypes();
                                  setTaskTypes(Array.isArray(list) ? list : list.results || []);
                                } catch (err) {
                                  alert(`Delete failed: ${err.message || err}`);
                                }
                              }}
                              style={{
                                padding: "4px 10px",
                                border: "1px solid #fecaca",
                                color: "#dc2626",
                                background: "transparent",
                                borderRadius: 4,
                                cursor: "pointer",
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}